import clsx from 'clsx';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';

import { SDSIconButton } from '@repo/sds/SDSIconButton';

import type { WidthProp } from '../../../types/common-props';
import { SearchBarDropdown } from '../../elements/SearchBar/SearchBarDropdown/SearchBarDropdown';

import './SingleChoiceDropdown.scss';

import { DataTestIds } from '@repo/frontend-shared';

import { getOptionLabel } from './single-choice-dropdown-utils';
import { useSingleChoiceDropdown } from './use-single-choice-dropdown';

export type SelectOptionValue = string | number | boolean;

export interface Option<T extends SelectOptionValue = string> {
  name: string;
  id: T;
}

export interface SingleChoiceDropdownProps<T extends SelectOptionValue = string> {
  getOptions: (search: string) => Promise<Option<T>[]> | Option<T>[];
  onChange?: (option: Option<T> | null) => void;
  inputPlaceholder?: string;
  isDisabled?: boolean;
  width?: WidthProp;
  name?: string;
  value?: T | Option<T> | null;
  hideDropdownArrow?: boolean;
  dataTestId?: string;
  ariaLabel?: string;
}

export function SingleChoiceDropdown<T extends SelectOptionValue = string>({
  name,
  getOptions,
  onChange,
  inputPlaceholder = 'Select',
  isDisabled = false,
  width = '100%',
  value,
  hideDropdownArrow = false,
  dataTestId,
  ariaLabel,
}: SingleChoiceDropdownProps<T>) {
  const {
    isDropdownOpen,
    singleChoiceDropdownRef,
    filteredOptions,
    search,
    handleSearchChange,
    handleOptionClick,
    handleClickOutside,
    toggleDropdown,
    openDropdown,
  } = useSingleChoiceDropdown({ value, getOptions, onChange, isDisabled });

  return (
    <div
      className={clsx('SingleChoiceDropdown', {
        'SingleChoiceDropdown--disabled': isDisabled,
      })}
      style={{ width }}
      ref={singleChoiceDropdownRef}
    >
      <div className="SingleChoiceDropdown__input-wrapper">
        <input
          id={name}
          aria-label={ariaLabel ?? inputPlaceholder}
          value={search}
          onChange={handleSearchChange}
          onFocus={openDropdown}
          // Focus alone cannot reopen a field that never lost focus, which is
          // where Escape leaves it.
          onClick={openDropdown}
          className="SingleChoiceDropdown__input"
          placeholder={inputPlaceholder}
          disabled={isDisabled}
          data-testid={dataTestId}
        />
      </div>
      {!hideDropdownArrow && (
        <SDSIconButton
          className="SingleChoiceDropdown__icon"
          icon={isDropdownOpen ? <ChevronUp /> : <ChevronDown />}
          onClick={toggleDropdown}
          isDisabled={isDisabled}
          ariaLabel={
            isDropdownOpen
              ? `Close ${ariaLabel ?? inputPlaceholder} options`
              : `Open ${ariaLabel ?? inputPlaceholder} options`
          }
          size="small"
        />
      )}
      <SearchBarDropdown
        opened={isDropdownOpen && !isDisabled}
        anchorRef={singleChoiceDropdownRef}
        options={filteredOptions}
        onChange={handleOptionClick}
        onClickOutside={handleClickOutside}
        getOptionLabel={getOptionLabel}
        getOptionKey={option => String(option.id)}
        search={search}
        dataTestId={DataTestIds.COMMON.DROPDOWN}
      />
    </div>
  );
}
